import { forceRefreshToken, logoutUser } from "@/lib/auth";

export interface FetchWithAuthDeps {
  forceRefreshToken: () => Promise<string>;
  logoutUser: () => void;
}

const defaultDeps: FetchWithAuthDeps = { forceRefreshToken, logoutUser };

/**
 * Perform a fetch with automatic 401 retry.
 * On first 401 → force-refresh the access token and retry once.
 * On second 401 (or refresh failure) → log out the user.
 */
export async function fetchWithAuth(
  url: string,
  init: RequestInit,
  token: string | undefined,
  deps: FetchWithAuthDeps = defaultDeps,
): Promise<Response> {
  const response = await fetch(url, {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401) {
    try {
      const freshToken = await deps.forceRefreshToken();
      const retry = await fetch(url, {
        ...init,
        headers: {
          Authorization: `Bearer ${freshToken}`,
          ...init.headers,
        },
      });
      if (retry.status === 401) {
        deps.logoutUser();
        throw new Error("Session expired");
      }
      if (!retry.ok) {
        throw new Error(`Request failed: ${retry.statusText}`);
      }
      return retry;
    } catch (e) {
      if (e instanceof Error && e.message === "Session expired") throw e;
      deps.logoutUser();
      throw new Error("Session expired");
    }
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${response.statusText}`);
  }

  return response;
}
